import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Tooltip } from '~/components';
import type { DropdownOption } from '~/components/Dropdown/types/DropdownOption';
import { FormFieldDropdownTreeSingleSelect } from '~/components/FormField/components/FormFieldDropdownTreeSingleSelect';
import { FormFieldInput } from '~/components/FormField/components/FormFieldInput';
import { IconButton } from '~/components/IconButton';
import { cn } from '~/lib/cn';
import { formatCurrency } from '~/modules/artist-roster/pages/artist-details/pages/artist-benchmarks/lib/utils';
import { PLATFORM_TABLE_GRID_COLUMNS } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';
import type { MediaPlanForm } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

import { MarketOptionTemplate } from './MarketOptionTemplate';

// @TODO WMG1-821 - Call API to get actual cost metrics
const MOCK_CPM = 0.29; // Cost Per Mille (per 1000 impressions)
const MOCK_CPC = 0.75; // Cost Per Click
const MOCK_CPV = 0.15; // Cost Per View
const MOCK_CPE = 0.12; // Cost Per Engagement
const mockCostPerResult = 0.07; // Cost Per Result
const mockResults = 2300000; // Results
const mockResultRate = 28; // Result Rate

interface MediaPlanRowProps {
  platformIndex: number;
  rowIndex: number;
  fieldId: string;
  fieldGoal?: string;
  marketOptions: DropdownOption[];
  goalOptions: DropdownOption[];
  onDuplicate: () => void;
  onDelete: () => void;
}

export function MediaPlanRow({
  platformIndex,
  rowIndex,
  fieldId,
  fieldGoal,
  marketOptions,
  goalOptions,
  onDuplicate,
  onDelete,
}: MediaPlanRowProps) {
  const { t } = useTranslation('campaign-planner');
  const { control } = useFormContext<MediaPlanForm>();

  const budget = useWatch({
    control,
    name: `platforms.${platformIndex}.plans.${rowIndex}.budget`,
    defaultValue: 0,
  });

  const frequency = useWatch({
    control,
    name: `platforms.${platformIndex}.plans.${rowIndex}.frequency.times`,
    defaultValue: 1,
  });

  const impressions = (budget * MOCK_CPM) / 1000;

  const reach = impressions / frequency;

  const videoViews = budget * MOCK_CPV;

  const clicks = budget * MOCK_CPC;

  const engagements = budget * MOCK_CPE;

  return (
    <div
      key={fieldId}
      className={cn(
        'grid gap-sm rounded-sm px-sm',
        rowIndex % 2 === 0 && 'bg-emphasis-default',
        'h-[56px] items-center',
      )}
      style={{ gridTemplateColumns: PLATFORM_TABLE_GRID_COLUMNS }}
    >
      {/* Budget */}
      {/* @TODO WMG1-820 - Add currency formatting */}
      <FormFieldInput<MediaPlanForm>
        name={`platforms.${platformIndex}.plans.${rowIndex}.budget`}
        label=""
        type="number"
        className="w-[112px] gap-y-0"
        triggerClassName="p-sm"
      />

      {/* Market */}
      <FormFieldDropdownTreeSingleSelect<DropdownOption, MediaPlanForm>
        name={`platforms.${platformIndex}.plans.${rowIndex}.market`}
        label=""
        className="gap-y-0"
        options={marketOptions}
        searchOptions={{ isEnabled: true }}
        OptionTemplate={MarketOptionTemplate}
        radix={{
          selectTrigger: {
            className: cn(
              'h-[28px] min-h-[28px] w-[150px] rounded-lg border-0 bg-transparent hover:bg-emphasis-default',
            ),
          },
          selectValue: {
            placeholder: t(
              'digitalCampaigns.mediaPlan.platforms.market.placeholder',
            ),
          },
          scrollAreaViewport: {
            className: 'max-h-[300px]',
          },
        }}
      />

      {/* Goal */}
      <FormFieldDropdownTreeSingleSelect<DropdownOption, MediaPlanForm>
        name={`platforms.${platformIndex}.plans.${rowIndex}.goal`}
        label=""
        className="gap-y-0"
        options={goalOptions}
        radix={{
          selectTrigger: {
            className: cn(
              'h-[28px] min-h-[28px] w-[134px] max-w-[134px] rounded-lg border-0 bg-transparent hover:bg-emphasis-default',
            ),
          },
          selectValue: {
            placeholder: t(
              'digitalCampaigns.mediaPlan.platforms.goals.placeholder',
            ),
          },
        }}
      />

      {/* Custom audience size */}
      <FormFieldInput<MediaPlanForm>
        name={`platforms.${platformIndex}.plans.${rowIndex}.audienceSize`}
        label=""
        type="number"
        className="w-[112px] gap-y-0"
        triggerClassName="p-sm"
      />

      {/* Frequency */}
      <FormFieldInput<MediaPlanForm>
        name={`platforms.${platformIndex}.plans.${rowIndex}.frequency.times`}
        label=""
        type="number"
        className="w-[42px] gap-y-0"
        triggerClassName="p-sm"
      />

      {/* Results */}
      <div className="w-[84px] leading-tight">
        <div className="text-body-sm font-semibold">
          {mockResults ? formatMillionsNumber(mockResults) : '-'}
        </div>
        <div className="text-caption-md">
          {goalOptions.find((option) => option.value === fieldGoal)?.label}
        </div>
      </div>
      {/* Cost per result */}
      <div className="text-left text-body-sm font-semibold">
        {formatCurrency({ value: mockCostPerResult })}
      </div>
      {/* Result rate */}
      <div className="text-left text-body-sm font-semibold">
        {mockResultRate}%
      </div>
      {/* Impressions */}
      <div className="text-left text-body-sm font-semibold">
        {impressions ? formatMillionsNumber(impressions) : '-'}
      </div>
      {/* Reach */}
      <div className="text-left text-body-sm font-semibold">
        {reach ? formatMillionsNumber(reach) : '-'}
      </div>
      {/* Video views */}
      <div className="text-left text-body-sm font-semibold">
        {videoViews ? formatMillionsNumber(videoViews) : '-'}
      </div>
      {/* Clicks */}
      <div className="text-left text-body-sm font-semibold">
        {clicks ? formatMillionsNumber(clicks) : '-'}
      </div>
      {/* Engagement */}
      <div className="text-left text-body-sm font-semibold">
        {engagements ? formatMillionsNumber(engagements) : '-'}
      </div>

      {/* Actions */}
      <div className="flex gap-sm">
        <Tooltip
          content={t('digitalCampaigns.mediaPlan.platforms.duplicateRow')}
          side="top"
          delayDuration={0}
          triggerAsChild={true}
          size="sm"
          contentClassName="mb-sm"
        >
          <IconButton
            iconType="copy"
            size="medium"
            iconVariant="light"
            onClick={onDuplicate}
            aria-label={t('digitalCampaigns.mediaPlan.platforms.duplicateRow')}
            className="size-[40px] shrink-0 p-0 duration-300 ease-out"
          />
        </Tooltip>
        <Tooltip
          content={t('digitalCampaigns.mediaPlan.platforms.deleteRow')}
          side="top"
          delayDuration={0}
          triggerAsChild={true}
          size="sm"
          contentClassName="mb-sm"
        >
          <IconButton
            iconType="trash"
            size="medium"
            iconVariant="light"
            onClick={onDelete}
            aria-label={t('digitalCampaigns.mediaPlan.platforms.deleteRow')}
            className="size-[40px] shrink-0 p-0 duration-300 ease-out"
          />
        </Tooltip>
      </div>
    </div>
  );
}

MediaPlanRow.displayName = 'MediaPlanRow';
